import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, LogOut, PencilLine, Save, Undo2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, setSite } from "../../lib/api";
import { applyTheme } from "../../context/SiteContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { BrandingTab } from "./BrandingTab";
import { ThemeTab } from "./ThemeTab";
import { ContentTab } from "./ContentTab";
import { HotelsTab } from "./HotelsTab";
import { PaymentsTab } from "./PaymentsTab";
import { ReservationTab } from "./ReservationTab";
import { BookingsTab } from "./BookingsTab";
import { EmailTab } from "./EmailTab";
import { EmbedTab } from "./EmbedTab";
import { DesignTab } from "./DesignTab";
import { designById } from "../../lib/designs";
import { ChangePasswordButton, ForcePasswordChange } from "./ChangePassword";

export const useMe = () => useQuery({ queryKey: ["me"], queryFn: () => api.get("/auth/me").then((r) => r.data), retry: false, staleTime: 60_000 });

const WebsiteLink = ({ slug }) => {
  const { data } = useQuery({ queryKey: ["websites"], queryFn: () => api.get("/web").then((r) => r.data) });
  const w = data?.find((x) => x.site_slug === slug);
  return w ? <Link to={`/website-admin/${w.id}`} className="btn-ghost" data-testid="admin-website-content"><PencilLine className="h-4 w-4" />Website</Link> : null;
};

const Editor = ({ saved, catalog, info, isSuper }) => {
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (section) => (p) => setDraft((d) => ({ ...d, [section]: { ...d[section], ...p } }));

  useEffect(() => {
    const t = draft.theme;
    const dark = parseInt(t.ink.slice(1, 3), 16) > 160;
    applyTheme(dark ? { ...t, background: "#FBF9F4", surface: "#F6F1E7", ink: "#2B2B2B", primary_light: "#F3EEE4" } : t);
  }, [draft.theme]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", draft);
      qc.setQueryData(["admin-settings"], data);
      qc.invalidateQueries({ queryKey: ["settings"] });
      setDraft(data);
      toast.success("Changes published");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const logout = async () => {
    await api.post("/auth/logout");
    qc.removeQueries();
    navigate("/admin/login");
  };

  return (
    <Tabs defaultValue="branding" className="min-h-screen bg-linen font-sans text-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-white/90 backdrop-blur-xl">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-3">
            {draft.brand.logo_url && <img src={draft.brand.logo_url} alt="" className="h-10 w-auto" />}
            <div><p className="font-serif text-lg leading-tight">{draft.brand.name}</p><p className="eyebrow">Site admin</p></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isSuper && <Link to="/superadmin" className="btn-ghost" data-testid="admin-all-sites"><ArrowLeft className="h-4 w-4" />All sites</Link>}
            <Link to={`/s/${info.slug}`} target="_blank" className="btn-ghost" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />View site</Link>
            <WebsiteLink slug={info.slug} />
            <ChangePasswordButton />
            <button className="btn-outline !py-2" disabled={!dirty} onClick={() => setDraft(saved)} data-testid="admin-discard"><Undo2 className="h-4 w-4" />Discard</button>
            <button className="btn-primary !py-2 disabled:opacity-40" disabled={!dirty || busy} onClick={save} data-testid="admin-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save
            </button>
            <button className="btn-ghost" onClick={logout} title="Sign out" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="container-x overflow-x-auto">
          <TabsList className="h-auto bg-transparent p-0">
            {[["design", "Design"], ["branding", "Branding"], ["theme", "Theme"], ["content", "Home page"], ["hotels", "Hotels & rooms"], ["payments", "Payments"], ["reservations", "Reservation system"], ["email", "Email"], ["embed", "Embed"], ["bookings", "Bookings"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:shadow-none">{l}</TabsTrigger>
            ))}
          </TabsList>
        </div>
      </header>
      <div className="container-x py-8">
        <TabsContent value="design"><DesignTab design={draft.design || "classic"} savedDesign={saved.design || "classic"} slug={info.slug}
          heroText={draft.hero_text || {}} onHeroText={(hero_text) => setDraft((d) => ({ ...d, hero_text }))}
          onPick={(id, keep) => setDraft((d) => ({ ...d, design: id, theme: keep || id === (saved.design || "classic") ? saved.theme : designById(id).theme }))} /></TabsContent>
        <TabsContent value="branding"><BrandingTab brand={draft.brand} set={set("brand")} /></TabsContent>
        <TabsContent value="theme"><ThemeTab theme={draft.theme} set={set("theme")} fonts={catalog.fonts} /></TabsContent>
        <TabsContent value="content">
          <ContentTab slides={draft.slides} news={draft.news}
            setSlides={(slides) => setDraft((d) => ({ ...d, slides }))} setNews={(news) => setDraft((d) => ({ ...d, news }))} />
        </TabsContent>
        <TabsContent value="hotels">
          <HotelsTab catalog={catalog.hotels} provider={catalog.provider} ownerContent={catalog.owner_content || {}} ownerValues={catalog.owner_values || {}} overrides={draft.hotels} setOverrides={(hotels) => setDraft((d) => ({ ...d, hotels }))} />
        </TabsContent>
        <TabsContent value="payments"><PaymentsTab /></TabsContent>
        <TabsContent value="reservations"><ReservationTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="embed"><EmbedTab info={info} primary={draft.theme.primary} /></TabsContent>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
      </div>
    </Tabs>
  );
};

export default function AdminDashboard() {
  const me = useMe();
  const qc = useQueryClient();
  const site = new URLSearchParams(useLocation().search).get("site") || "";
  const isSuper = me.data?.platform_role === "superadmin";
  const ready = !!me.data && (!isSuper || !!site) && !me.data.must_change_password;
  setSite(isSuper ? site : "");
  useEffect(() => () => qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" }), [qc]);
  const settings = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data), enabled: ready });
  const info = useQuery({ queryKey: ["admin-site"], queryFn: () => api.get("/admin/site").then((r) => r.data), enabled: ready });
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => api.get("/admin/catalog").then((r) => r.data), enabled: ready });
  if (me.isError) return <Navigate to="/admin/login" replace />;
  if (me.data && isSuper && !site) return <Navigate to="/superadmin" replace />;
  if (me.data?.must_change_password && !isSuper) return <ForcePasswordChange email={me.data.email} />;
  const err = settings.error || info.error;
  if (err) {
    const out = async () => {
      await api.post("/auth/logout");
      qc.removeQueries();
      window.location.href = "/admin/login";
    };
    return <div className="grid min-h-screen place-items-center p-8 text-center" data-testid="admin-site-error">
      <div><p>{apiError(err)}</p><button className="btn-outline mt-4" onClick={out} data-testid="admin-error-logout"><LogOut className="h-4 w-4" />Sign out</button></div>
    </div>;
  }
  if (!me.data || !settings.data || !info.data || !(catalog.data || catalog.isError)) {
    return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  }
  return <Editor saved={settings.data} catalog={catalog.data || { hotels: [], fonts: [], provider: "" }} info={info.data} isSuper={isSuper} />;
}
